import { Star, Trash2, MessageSquare, Loader } from "lucide-react";
import DeleteReviewModal from "../../modals/reviewModal/deleteModal";
import { useReviews } from "../../hooks/useReview";
import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import Pagination from "../../components/Pagination";

const Reviews = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const { reviews, loading, error, pagination } = useReviews(currentPage);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedReview, setSelectedReview] = useState(null);
  const queryClient = useQueryClient();

  return (
    <div className="min-h-screen p-4 sm:p-6 md:p-8">
      {/* --- HEADER --- */}
      <div className="mx-auto mb-4 sm:mb-6">
        <h1 className="text-lg sm:text-xl font-medium text-[#0C0C0E]">
          Reviews
        </h1>
        <p className="font-medium text-xs sm:text-sm text-[#7D828D]">
          Moderate customer reviews
        </p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader size={32} className="text-[#6B7143] animate-spin" />
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 text-red-600 px-4 sm:px-6 py-4 rounded-lg text-xs sm:text-sm">
          {error}
        </div>
      ) : reviews.length > 0 ? (
        <div className="mx-auto space-y-4">
          {reviews.map((review) => (
            <div
              key={review.id}
              className="bg-white rounded-2xl border border-slate-200 p-4 sm:p-6 shadow-sm relative"
            >
              <div className="flex flex-col sm:flex-row justify-between items-start mb-2 gap-2">
                <div className="flex items-center gap-2 sm:gap-3">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold ${review.id % 2 === 0 ? "bg-rose-300" : "bg-[#6B7143]"}`}
                  >
                    {review.user?.name?.charAt(0)?.toUpperCase?.()}
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-900 text-xs sm:text-sm">
                      {review.user?.name || review.name || "Unknown User"}
                    </h3>
                    <p className="text-[10px] sm:text-[11px] text-slate-400 font-medium">
                      {review.created_at ? review.created_at.slice(0, 10) : ""}
                    </p>
                  </div>
                </div>
              </div>

              <div className="ml-0 sm:ml-13">
                <p className="text-xs sm:text-sm font-medium text-[#6B7143] mb-2 sm:mb-3.5">
                  {review.product?.title || review.product_name || "Product"}
                </p>
                <div className="flex items-center gap-1 mb-1">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      size={14}
                      className={
                        i < (review.rating || review.stars || 0)
                          ? "fill-amber-400 text-amber-400"
                          : "text-slate-200"
                      }
                    />
                  ))}
                </div>
                <p className="text-xs sm:text-sm text-[#7D828D] font-medium mt-2">
                  "
                  {review.review ||
                    "No review description provided."}
                  "
                </p>

                <div className="mt-2 flex flex-col sm:flex-row justify-start items-start sm:items-center border-t border-[#ECEDEF] pt-3 gap-2 sm:gap-2">
                  <button
                    className="text-rose-500 flex flex-row items-center gap-2 bg-[#FBF8F6] border border-[#F1EBE2] hover:bg-rose-50 p-2 rounded-lg transition-colors active:scale-90 duration-100 text-xs sm:text-sm"
                    onClick={() => {
                      setSelectedReview(review);
                      setShowDeleteModal(true);
                    }}
                  >
                    Delete <Trash2 size={18} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        /* Empty State */
        <div className="flex flex-col items-center justify-center py-12 sm:py-16 px-4 sm:px-6 border border-[#EEF1F3] rounded-lg">
          <div className="p-1 border border-slate-300 bg-[#F8F5F1] rounded-full overflow-hidden mb-4">
            <MessageSquare size={32} className="text-[#BFAC93]" />
          </div>
          <h4 className="font-medium text-sm sm:text-base text-[#0C0C0E] text-center mb-2">
            No Reviews yet
          </h4>
          <p className="text-xs sm:text-sm text-slate-400 text-center">
            Reviews will appear here once customers give feedback
          </p>
        </div>
      )}

      {/* --- DELETE MODAL --- */}
      <DeleteReviewModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        review={selectedReview}
        queryClient={queryClient}
        onDeleteSuccess={() => setShowDeleteModal(false)}
      />

      {/* --- PAGINATION --- */}
      {pagination && (
        <div className="p-4 sm:p-6 flex flex-col sm:flex-row gap-3 sm:gap-0 items-center justify-between border-t border-[#EEF1F3] bg-[#FBF8F6] mt-8 w-full">
          <span className="text-xs sm:text-sm text-[#7D828D]">
            Showing{" "}
            {pagination.current_page
              ? (pagination.current_page - 1) * pagination.per_page + 1
              : 0}{" "}
            to{" "}
            {Math.min(
              pagination.current_page * pagination.per_page,
              pagination.total,
            )}{" "}
            of {pagination.total} reviews
          </span>
          <div className="w-full sm:w-auto">
            <Pagination
              currentPage={pagination.current_page}
              totalPages={pagination.last_page}
              onPageChange={setCurrentPage}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Reviews;
